import { type FC, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";

import { AccountCircleIcon } from "@moment/icons";

import { Button } from "../../Button";
import { useCellServices } from "../../CellServicesContext";
import { Chip, stringToColorVariant } from "../../Chip";
import { DropdownMenu } from "../../DropdownMenu";
import { AvatarSize } from "../../MomentAvatar";
import { ReactionAlpha } from "../../ReactionAlpha";
import {
	CLEAR_SELECT_ITEM_VALUE,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "../../Select";
import { TextboxAuto } from "../../TextboxAuto";
import { UserAvatarAlpha } from "../../UserAvatarAlpha";
import { cn } from "../../utils";
import { type HydratedCell } from "../HydratedCell";

export const DialogContentCell: FC<{
	htmlId?: string;
	className?: string;
	allowDataEdits: boolean;
	cell: HydratedCell;
	primaryDisplayColumn?: boolean;
}> = ({ className, cell, primaryDisplayColumn, htmlId }) => {
	const { currentUser, users } = useCellServices();

	const debouncedUpdate = useDebouncedCallback(
		"update" in cell ? cell.update : cell.toggleReaction,
		300
	);

	// Cancel pending debounced updates when component unmounts or recordId changes
	useEffect(() => {
		return () => {
			debouncedUpdate.cancel();
		};
	}, [debouncedUpdate, cell.type === "Text" ? cell.recordId : null]);

	switch (cell.type) {
		case "Text":
			return (
				<TextboxAuto
					id={htmlId}
					className={cn("bg-transparent", className)}
					defaultValue={cell.value}
					onKeyDown={(e) => {
						if (e.key === "Enter" && primaryDisplayColumn) {
							e.preventDefault();
							// Find the next focusable element in the dialog
							const dialog = e.currentTarget.closest('[role="dialog"]');
							if (dialog) {
								const focusableElements = Array.from(
									dialog.querySelectorAll(
										'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
									)
								).filter((el) => !el.hasAttribute("disabled")) as HTMLElement[];

								const currentIndex = focusableElements.findIndex(
									(el) => el === e.currentTarget
								);

								// If we're at the last element, wrap to the first, otherwise go to next
								const nextIndex =
									currentIndex === focusableElements.length - 1
										? 0
										: currentIndex + 1;
								const nextElement = focusableElements[nextIndex];
								nextElement?.focus();
							}
						}
					}}
					onChange={(e) => debouncedUpdate(e.target.value)}
					disabled={cell.disabled}
					key={cell.recordId}
				/>
			);
		case "Reaction":
			return (
				<ReactionAlpha
					id={htmlId}
					size="sm"
					currentUser={currentUser}
					reactionsValue={cell.value.map(({ user }) => user)}
					onChangeReactions={() => currentUser && cell.toggleReaction(currentUser)}
					disabled={cell.disabled}
				/>
			);
		case "Select": {
			const cellIsEmpty =
				cell.value === CLEAR_SELECT_ITEM_VALUE || cell.value === null || cell.value === "";

			return (
				<Select value={cell.value ?? CLEAR_SELECT_ITEM_VALUE} onValueChange={cell.update}>
					<SelectTrigger
						id={htmlId}
						className="pl-2 -mr-1 w-fit"
						disabled={cell.disabled}
					>
						{cellIsEmpty ? (
							<span className="text-secondary">Empty</span>
						) : (
							<div className="-mx-1">
								<Chip
									level="secondary"
									size="md"
									fontVariant="no-caps"
									colorVariant={
										cell.typeInfo?.options.find(
											({ label }) => label === cell.value
										)?.color ?? stringToColorVariant(cell.value)
									}
								>
									{cell.value}
								</Chip>
							</div>
						)}
					</SelectTrigger>
					<SelectContent withPortal={false}>
						<SelectItem value={CLEAR_SELECT_ITEM_VALUE}>Blank</SelectItem>
						{cell.typeInfo?.options.map(
							({ label, color }) =>
								label && (
									<SelectItem key={label} value={label}>
										<Chip
											level="secondary"
											size="md"
											fontVariant="no-caps"
											colorVariant={color}
										>
											{label}
										</Chip>
									</SelectItem>
								)
						)}
					</SelectContent>
				</Select>
			);
		}
		case "Person": {
			const update = (userId: string | null) => {
				const user = userId ? (users.find((u) => u.id === userId) ?? null) : null;
				cell.update(user);
			};
			return (
				<div>
					<DropdownMenu.Root>
						<DropdownMenu.Trigger asChild disabled={cell.disabled}>
							<Button id={htmlId} size="md" preset="secondary" elevation="flat">
								{!cell.value ? (
									<>
										<AccountCircleIcon />
										<span className="!text-tertiary">Empty</span>
									</>
								) : (
									<>
										<UserAvatarAlpha
											size={AvatarSize.xxs}
											imageURL={cell.value?.profilePicture}
											label={cell.value?.name}
										/>
									</>
								)}
							</Button>
						</DropdownMenu.Trigger>
						<DropdownMenu.Content>
							<DropdownMenu.Item
								className="text-secondary"
								onClick={() => update(null)}
							>
								<AccountCircleIcon className="m-0.5" /> Blank
							</DropdownMenu.Item>
							{users.map((user) => (
								<DropdownMenu.Item key={user.id} onClick={() => update(user.id)}>
									<UserAvatarAlpha
										size={AvatarSize.xs}
										imageURL={user.profilePicture}
										label={user.name}
									/>
								</DropdownMenu.Item>
							))}
						</DropdownMenu.Content>
					</DropdownMenu.Root>
				</div>
			);
		}
		default: {
			const _exhaustive: never = cell;
			throw new Error(`Unknown cell type ${(_exhaustive as HydratedCell)?.type}`);
		}
	}
};
